import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ArrowLeft, 
  DollarSign, 
  TrendingUp, 
  Calendar,
  FileText,
  Download
} from 'lucide-react';
import './CustomerDetail.css';

const CustomerDetail = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const apiService = useApiService();
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    loadCustomerData();
    loadUserInfo();
  }, [customerId]);

  const loadUserInfo = async () => {
    try {
      // Get the current logged-in user from the database using /me endpoint
      const userData = await apiService.getCurrentUser();
      console.log('Current user data loaded from database:', userData);
      setUserInfo(userData);
    } catch (error) {
      console.error('Error loading current user info:', error);
      // Set default user info if API call fails
      setUserInfo({
        name: currentUser?.name || 'User',
        email: currentUser?.email || 'user@example.com',
        companyName: 'Company Name'
      });
    }
  };

  const loadCustomerData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      console.log('Loading customer data for ID:', customerId);
      console.log('With params:', params);
      
      const response = await apiService.getCustomerPaymentReport(customerId, params);
      console.log('API Response:', response);
      setCustomerData(response);
    } catch (error) {
      console.error('Error loading customer data:', error);
      setError('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    
    // Trigger data loading without showing loading state
    setTimeout(() => {
      loadCustomerDataWithoutLoading();
    }, 100);
  };

  const loadCustomerDataWithoutLoading = async () => {
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      
      const response = await apiService.getCustomerPaymentReport(customerId, params);
      setCustomerData(response);
    } catch (error) {
      console.error('Error loading customer data:', error);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'Rs. 0';
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-PK');
  };

  const getProfitColor = (amount) => {
    if (!amount || amount === 0) return '#6b7280';
    return amount > 0 ? '#10b981' : '#ef4444';
  };

  // Export functionality
  const exportToCSV = () => {
    if (!customerData || dealHistory.length === 0) {
      alert('No data to export');
      return;
    }

    // Create CSV headers
    const headers = [
      'Deal ID',
      'Type',
      'Car Name',
      'Registration Number',
      'Date',
      'Total Amount',
      'Paid/Received',
      'Due Amount'
    ];

    // Create CSV rows
    const csvRows = [
      headers.join(','),
      ...dealHistory.map(deal => [
        `"${deal.dealId}"`,
        `"${deal.dealType}"`,
        `"${deal.car?.name || 'N/A'}"`,
        `"${deal.car?.registrationNumber || 'N/A'}"`,
        `"${deal.dealDate ? formatDate(deal.dealDate) : 'N/A'}"`,
        deal.totalAmount || 0,
        deal.dealType === 'SALE' ? (deal.receivedAmount || 0) : (deal.givenAmount || 0),
        deal.dueAmount || 0
      ].join(','))
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${customer.fullName}_deal_history_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // PDF Export functionality
  const exportToPDF = async () => {
    if (!customerData || dealHistory.length === 0) {
      alert('No data to export');
      return;
    }

    try {
      // Dynamic import for jsPDF and html2canvas
      const { default: jsPDF } = await import('jspdf');
      const html2canvas = await import('html2canvas');

      // Create a temporary div for the PDF content
      const pdfContent = document.createElement('div');
      pdfContent.style.position = 'absolute';
      pdfContent.style.left = '-9999px';
      pdfContent.style.top = '0';
      pdfContent.style.width = '1200px'; // Increased width for landscape mode
      pdfContent.style.padding = '20px';
      pdfContent.style.fontFamily = 'Arial, sans-serif';
      pdfContent.style.backgroundColor = 'white';
      pdfContent.style.color = 'black';

      // Get user information
      const userName = userInfo?.name || currentUser?.name || 'User';
      const userEmail = userInfo?.email || currentUser?.email || 'user@example.com';
      const companyName = userInfo?.companyName || 'Company Name';

      pdfContent.innerHTML = `
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px;">
          <div style="font-size: 24px; font-weight: bold; margin-bottom: 5px;">${companyName}</div>
          <div style="font-size: 20px; margin-bottom: 10px;">Customer Payment Report</div>
          <div style="font-size: 12px; color: #666; margin-bottom: 5px;">Generated on: ${new Date().toLocaleString('en-PK')}</div>
        </div>
        
        <div style="margin-bottom: 20px; padding: 15px; background-color: #f9f9f9; border-radius: 5px;">
          <h3 style="margin-top: 0;">Customer Information</h3>
          <p style="margin: 5px 0;"><strong>Name:</strong> ${customer.fullName}</p>
          <p style="margin: 5px 0;"><strong>CNIC:</strong> ${customer.cnic || 'N/A'}</p>
          <p style="margin: 5px 0;"><strong>Phone:</strong> ${customer.phoneNumber}</p>
          <p style="margin: 5px 0;"><strong>Client Type:</strong> ${customer.clientType}</p>
        </div>
        
        <div style="margin-bottom: 20px;">
          <h3 style="margin-top: 0;">Summary</h3>
          <div style="margin-bottom: 10px;"><strong>Total To Give:</strong> ${formatCurrency(purchaseSummary?.dueAmount || 0)}</div>
          <div style="margin-bottom: 10px;"><strong>Total To Receive:</strong> ${formatCurrency(saleSummary?.dueAmount || 0)}</div>
          <div style="margin-bottom: 10px;"><strong>Purchase Summary:</strong> ${formatCurrency(purchaseSummary?.dueAmount || 0)} (${purchaseSummary?.deals || 0} deals)</div>
          <div style="margin-bottom: 10px;"><strong>Sale Summary:</strong> ${formatCurrency(saleSummary?.dueAmount || 0)} (${saleSummary?.deals || 0} deals)</div>
          <div style="margin-bottom: 10px;"><strong>Total Deals:</strong> ${dealHistory.length}</div>
        </div>
        
        <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
          <thead>
            <tr style="background-color: #f2f2f2;">
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Deal ID</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Type</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Car Name</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Registration Number</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Date</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Total Amount</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Paid/Received</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Due Amount</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">To Give/Receive</th>
            </tr>
          </thead>
          <tbody>
            ${dealHistory.map(deal => `
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${deal.dealId}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${deal.dealType}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${deal.car?.name || 'N/A'}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${deal.car?.registrationNumber || 'N/A'}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${deal.dealDate ? formatDate(deal.dealDate) : 'N/A'}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${formatCurrency(deal.totalAmount)}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${deal.dealType === 'SALE' ? formatCurrency(deal.receivedAmount) : formatCurrency(deal.givenAmount)}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${formatCurrency(deal.dueAmount)}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${deal.dealType === 'SALE' ? 'TO RECEIVE' : 'TO GIVE'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;

      document.body.appendChild(pdfContent);

      // Convert to canvas
      const canvas = await html2canvas.default(pdfContent, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      // Remove the temporary element
      document.body.removeChild(pdfContent);

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4'); // Changed to landscape mode
      const imgWidth = 297; // A4 landscape width
      const pageHeight = 210; // A4 landscape height
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Add footer to all pages
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text('powered by ledger Glide | +923332416545', imgWidth - 10, pageHeight - 5, { align: 'right' });
      }

      // Save the PDF
      pdf.save(`${customer.fullName}_deal_history_${new Date().toISOString().split('T')[0]}.pdf`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  if (loading) {
    return (
      <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', background: '#fafbfc', minHeight: '100vh', padding: '2rem 1.5rem', width: '100%', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem', color: '#6b7280' }}>
          <div style={{ width: '40px', height: '40px', border: '4px solid #e5e7eb', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '1rem' }}></div>
          <p>Loading customer data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', background: '#fafbfc', minHeight: '100vh', padding: '2rem 1.5rem', width: '100%', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem', color: '#dc2626' }}>
          <p>{error}</p>
          <button onClick={loadCustomerData} style={{ marginTop: '1rem', padding: '0.75rem 1.5rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '500', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!customerData) {
    return (
      <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', background: '#fafbfc', minHeight: '100vh', padding: '2rem 1.5rem', width: '100%', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem', color: '#dc2626' }}>
          <p>No customer data available</p>
          <button onClick={loadCustomerData} style={{ marginTop: '1rem', padding: '0.75rem 1.5rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '500', cursor: 'pointer' }}>
            Load Data
          </button>
        </div>
      </div>
    );
  }

  const { customer, purchaseSummary, saleSummary, dealHistory, netPosition } = customerData;

  // Debug: Log the data structure to see what we're getting
  // console.log('Customer Data:', customerData);
  // console.log('Purchase Summary:', purchaseSummary);
  // console.log('Sale Summary:', saleSummary);
  // console.log('Net Position:', netPosition);

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', background: '#fafbfc', minHeight: '100vh', padding: '2rem 1.5rem', width: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '.5rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.25rem' }}>
            <h1 className="fw-bold mb-2" style={{ color: '#1f2937', fontSize: '1.75rem', letterSpacing: '-0.025em', margin: 0 }}>{customer.fullName}</h1>
            <span style={{ 
              padding: '0.25rem 0.5rem', 
              borderRadius: '4px', 
              fontSize: '0.75rem', 
              fontWeight: '500',
              background: '#e0e7ff', 
              color: '#3730a3' 
            }}>
              {customer.clientType}
            </span>
          </div>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
            CNIC: {customer.cnic} | Phone: {customer.phoneNumber}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              background: '#f8fafc', 
              color: '#64748b', 
              border: '1px solid #e2e8f0', 
              borderRadius: '8px', 
              padding: '0.75rem 1.5rem', 
              fontSize: '0.875rem', 
              fontWeight: '600', 
              cursor: 'pointer' 
            }}
            onClick={() => navigate('/reporting')}
          >
            <ArrowLeft size={16} />
            Back to Reports
          </button>
          <button className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold" style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.875rem', boxShadow: '0 1px 3px 0 rgba(99, 102, 241, 0.1), 0 1px 2px 0 rgba(99, 102, 241, 0.06)' }} onClick={exportToCSV}>
            <Download size={16} />
            <span className="d-none d-sm-inline">Export CSV</span>
          </button>
          <button className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold" style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.875rem', boxShadow: '0 1px 3px 0 rgba(220, 38, 38, 0.1), 0 1px 2px 0 rgba(220, 38, 38, 0.06)' }} onClick={exportToPDF}>
            <FileText size={16} />
            <span className="d-none d-sm-inline">Export PDF</span>
          </button>
        </div>
      </div>

      {/* Date Filters - Moved above summary cards */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
          <Calendar size={20} style={{ color: '#6b7280' }} />
          <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>Date Filters</h3>
        </div>
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          flexWrap: 'wrap',
          background: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #f3f4f6'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: '1 1 200px', minWidth: 0 }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              style={{ 
                padding: '0.75rem', 
                border: '1px solid #d1d5db', 
                borderRadius: '8px', 
                fontSize: '0.875rem',
                background: 'white',
                width: '100%',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: '1 1 200px', minWidth: 0 }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              style={{ 
                padding: '0.75rem', 
                border: '1px solid #d1d5db', 
                borderRadius: '8px', 
                fontSize: '0.875rem',
                background: 'white',
                width: '100%',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        <div style={{ flex: '1 1 300px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center" style={{ marginBottom: '1rem' }}>
                <div style={{ width: 40, height: 40, background: '#fef3c7', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <DollarSign size={20} style={{ color: '#92400e' }} />
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Purchase Summary</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>{formatCurrency(purchaseSummary?.dueAmount || 0)}</p>
                </div>
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                <div style={{ marginBottom: '0.25rem' }}>
                  You have to give: {formatCurrency(purchaseSummary?.dueAmount || 0)}
                </div>
                <div style={{ marginBottom: '0.25rem' }}>Total deals: {purchaseSummary?.deals || 0}</div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1 1 300px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center" style={{ marginBottom: '1rem' }}>
                <div style={{ width: 40, height: 40, background: '#d1fae5', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <TrendingUp size={20} style={{ color: '#16a34a' }} />
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Sale Summary</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>{formatCurrency(saleSummary?.dueAmount || 0)}</p>
                </div>
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                <div style={{ marginBottom: '0.25rem' }}>
                  You have to receive: {formatCurrency(saleSummary?.dueAmount || 0)}
                </div>
                <div style={{ marginBottom: '0.25rem' }}>Total deals: {saleSummary?.deals || 0}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Deal History */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid #f3f4f6' }}>
          <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>Deal History</h3>
          <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>Detailed transaction history for this customer</p>
        </div>

        {dealHistory.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>No Deal History</h4>
            <p style={{ margin: 0, fontSize: '0.875rem' }}>No deals found for this customer in the selected date range.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Deal ID</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Type</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Car</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Date</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Total Amount</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Paid/Received</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Due</th>
              </tr>
            </thead>
            <tbody>
              {dealHistory.map((deal, index) => (
                <tr key={`${deal.dealId}-${index}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                    <span style={{ fontWeight: '600' }}>{deal.dealId}</span>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{ 
                      padding: '0.25rem 0.5rem', 
                      borderRadius: '4px', 
                      fontSize: '0.75rem', 
                      fontWeight: '500',
                      textTransform: 'uppercase',
                      background: deal.dealType === 'SALE' ? '#d1fae5' : '#fef3c7',
                      color: deal.dealType === 'SALE' ? '#065f46' : '#92400e'
                    }}>
                      {deal.dealType}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                    {deal.car ? (
                      <div>
                        <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{deal.car.name}</div>
                        <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>{deal.car.registrationNumber}</div>
                      </div>
                    ) : (
                      <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>No car info</span>
                    )}
                  </td>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                    {deal.dealDate ? formatDate(deal.dealDate) : 'N/A'}
                  </td>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                    {formatCurrency(deal.totalAmount)}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <span style={{ color: '#1f2937', fontSize: '0.875rem' }}>
                        {deal.dealType === 'SALE' 
                          ? formatCurrency(deal.receivedAmount)
                          : formatCurrency(deal.givenAmount)
                        }
                      </span>
                      <span style={{ 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px', 
                        fontSize: '0.75rem', 
                        fontWeight: '500',
                        textTransform: 'uppercase',
                        background: deal.dealType === 'SALE' ? '#d1fae5' : '#fef3c7',
                        color: deal.dealType === 'SALE' ? '#065f46' : '#92400e',
                        display: 'inline-block'
                      }}>
                        {deal.dealType === 'SALE' ? 'RECEIVED' : 'GIVEN'}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <span style={{ 
                        fontWeight: '600', 
                        color: deal.dueAmount > 0 ? '#dc2626' : '#16a34a',
                        fontSize: '0.875rem'
                      }}>
                        {formatCurrency(deal.dueAmount)}
                      </span>
                      {deal.dueAmount > 0 && (
                        <span style={{ 
                          padding: '0.25rem 0.5rem', 
                          borderRadius: '4px', 
                          fontSize: '0.75rem', 
                          fontWeight: '500',
                          textTransform: 'uppercase',
                          background: deal.dealType === 'SALE' ? '#fef3c7' : '#fef2f2',
                          color: deal.dealType === 'SALE' ? '#92400e' : '#dc2626',
                          display: 'inline-block'
                        }}>
                          {deal.dealType === 'SALE' ? 'TO RECEIVE' : 'TO GIVE'}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDetail; 